import { Box, Chip, IconButton } from "@mui/material"
import { ArchiveOutlined, DeleteOutlined } from "@mui-symbols-material/w300"
import type { CellContext, ColumnDef } from "@tanstack/react-table"
import React from "react"

import FileChip from "@/components/upload/components/display/FileChip"
import type { File, SignedUrl } from "@/graphql/codegen/graphql"
import { CompanyFileTypeMap } from "@/screens/Companies/edit/components/info/constants"
import { useCompanyFilesContext } from "@/screens/Companies/edit/components/info/context/CompanyFilesContext"
import { formatDate } from "@/utils/date"

const MENU_ICON_PROPS = {
  fontVariationSettings: '"wght" 300',
  color: "grey.700",
}
const useCompanyFilesColumns = ({
  isChangingStatus,
  handleFileStatusChangeConfirmationDialogOpen,
  nextFileStatusAction,
}: {
  isChangingStatus: boolean
  handleFileStatusChangeConfirmationDialogOpen: (file: File) => void
  nextFileStatusAction: string | null
}): ColumnDef<SignedUrl>[] => {
  const { isActiveCompany } = useCompanyFilesContext()
  return [
    {
      accessorKey: "fieldGroup",
      header: "Type",
      cell: ({ row: { original } }: CellContext<SignedUrl, unknown>) => {
        const data = CompanyFileTypeMap[original.file?.fieldGroup as keyof typeof CompanyFileTypeMap]
        return (
          <Chip
            label={data.label ?? original.file?.fieldGroup}
            variant='filled'
            size='small'
            sx={{ backgroundColor: data.color ?? "primary" }}
          />
        )
      },
      enableSorting: true,
      size: 150,
    },
    {
      accessorKey: "originalName",
      header: "File",
      cell: ({ row }: CellContext<SignedUrl, unknown>) => {
        const isArchived = row.original.file?.isArchived
        return (
          <Box className={`flex items-center gap-2 ${isArchived ? "opacity-60" : "opacity-100"}`}>
            <FileChip file={row.original} />
            {isActiveCompany && isArchived && (
              <Box className='flex items-center gap-1'>
                <ArchiveOutlined fontSize={"small"} />
                <span className='text-xs'>Archived File</span>
              </Box>
            )}
          </Box>
        )
      },
      enableSorting: true,
      size: 150,
    },
    {
      accessorKey: "createdOn",
      header: "Date Uploaded",
      cell: ({ row }: CellContext<SignedUrl, unknown>) => formatDate(row?.original?.file?.createdOn),
      enableSorting: true,
      size: 50,
    },
    {
      accessorKey: "actions",
      header: "",
      cell: ({ row }: CellContext<SignedUrl, unknown>) => (
        <Box className='flex flex-row items-center justify-end gap-1'>
          <IconButton
            color='primary'
            aria-label={`${nextFileStatusAction} file`}
            disabled={isChangingStatus}
            onClick={() => handleFileStatusChangeConfirmationDialogOpen(row.original.file as File)}
          >
            {isActiveCompany ? <ArchiveOutlined sx={MENU_ICON_PROPS} /> : <DeleteOutlined sx={MENU_ICON_PROPS} />}
          </IconButton>
        </Box>
      ),
      size: 50,
      enableSorting: false,
    },
  ]
}

export default useCompanyFilesColumns
